import { useEffect, useMemo, useState } from "react";
import { api, formatTRY, formatNum, downloadExport } from "@/lib/apiClient";
import { groupByCategory } from "@/lib/groupBy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ShoppingCart, Plus, Minus, Receipt, FileSpreadsheet, BadgePercent, UserRound, ArrowRight, Search, X } from "lucide-react";
import { OpenTabs } from "@/components/sales/OpenTabs";
import { Link } from "react-router-dom";
import { CustomerPicker } from "@/components/sales/CustomerPicker";
import { SalesList } from "@/components/sales/SalesList";
import { toast } from "sonner";
import { MobileCartBar } from "@/components/sales/MobileCartBar";

const PAYMENTS = ["Nakit", "Kredi Kartı", "Karma", "Açık Hesap", "Personel"];
const sortedVariants = (g) => [...g.variants].sort((a, b) => a.selling_price - b.selling_price);
const CART_KEY = "fh_pos_cart";
const readCart = () => { try { return JSON.parse(localStorage.getItem(CART_KEY)) || {}; } catch { return {}; } };

export default function Sales() {
  const [recipes, setRecipes] = useState([]);
  const [search, setSearch] = useState("");
  const norm = (s) => (s || "").replace(/[İIı]/g, "i").toLocaleLowerCase("tr").replace(/i̇/g, "i");
  const q = norm(search.trim());
  const visibleRecipes = q ? recipes.filter((r) => norm(r.product_name).includes(q) || norm(r.category).includes(q) || (r.flavors || []).some((f) => norm(f).includes(q))) : recipes;
  const visibleRowCount = new Set(visibleRecipes.map((r) => `${r.product_name.trim().toLowerCase()}|${r.category}`)).size;
  const saved = useMemo(readCart, []);
  const [cart, setCart] = useState(saved.cart || {});
  const [payment, setPayment] = useState(saved.payment || "Nakit");
  const [cashAmt, setCashAmt] = useState(saved.cashAmt || "");
  const [cardAmt, setCardAmt] = useState(saved.cardAmt || "");
  const [discType, setDiscType] = useState(saved.discType || "none");
  const [discValue, setDiscValue] = useState(saved.discValue || "");
  const [customer, setCustomer] = useState(saved.customer || "");
  const [memberId, setMemberId] = useState(saved.memberId || null);
  const [history, setHistory] = useState([]);
  const [openTabs, setOpenTabs] = useState([]);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify({ cart, payment, cashAmt, cardAmt, discType, discValue, customer, memberId }));
  }, [cart, payment, cashAmt, cardAmt, discType, discValue, customer, memberId]);

  const load = async () => {
    api.get("/recipes").then((r) => setRecipes(r.data));
    api.get("/sales/open").then((r) => setOpenTabs(r.data)).catch(() => {});
    const r = await api.get("/sales");
    setHistory(r.data.filter((s) => s.payment_type !== "Açık Hesap"));
    return r.data;
  };
  useEffect(() => { load(); }, []);

  const recMap = useMemo(() => Object.fromEntries(recipes.map((r) => [r.id, r])), [recipes]);

  const keyOf = (id, flavor = "") => (flavor ? `${id}|${flavor}` : id);
  const add = (id, flavor = "") => { const k = keyOf(id, flavor); setCart((c) => ({ ...c, [k]: (c[k] || 0) + 1 })); };
  const dec = (k) => setCart((c) => {
    const n = (c[k] || 0) - 1;
    const copy = { ...c };
    if (n <= 0) delete copy[k]; else copy[k] = n;
    return copy;
  });

  const cartItems = Object.entries(cart).map(([k, qty]) => {
    const [id, flavor = ""] = k.split("|");
    const r = recMap[id] || {};
    return { ...r, key: k, flavor, qty, display_name: flavor ? `${r.display_name || r.product_name} - ${flavor}` : (r.display_name || r.product_name) };
  });
  const totals = cartItems.reduce((a, it) => {
    a.total += it.selling_price * it.qty;
    a.cost += it.cost_price * it.qty;
    a.protein += it.total_protein * it.qty;
    a.vp += it.total_vp * it.qty;
    return a;
  }, { total: 0, cost: 0, protein: 0, vp: 0 });
  const discNum = Math.max(0, parseFloat(String(discValue).replace(",", ".")) || 0);
  const discountAmount = discType === "percent" ? totals.total * Math.min(discNum, 100) / 100
    : discType === "amount" ? Math.min(discNum, totals.total) : 0;
  const grandTotal = payment === "Personel" ? 0 : Math.max(0, totals.total - discountAmount);
  const num = (v) => Math.max(0, parseFloat(String(v).replace(",", ".")) || 0);
  const splitOk = payment !== "Karma" || Math.abs(num(cashAmt) + num(cardAmt) - grandTotal) < 0.01;
  const setCash = (v) => { setCashAmt(v); setCardAmt(String(Math.max(0, +(grandTotal - num(v)).toFixed(2)))); };
  const setCard = (v) => { setCardAmt(v); setCashAmt(String(Math.max(0, +(grandTotal - num(v)).toFixed(2)))); };

  const checkout = async () => {
    if (cartItems.length === 0) return toast.error("Sepet boş");
    if (!splitOk) return toast.error("Nakit + Kart toplamı fiş tutarına eşit olmalı");
    try {
      const r = await api.post("/sales", {
        payment_type: payment,
        cash_amount: payment === "Karma" ? num(cashAmt) : 0,
        card_amount: payment === "Karma" ? num(cardAmt) : 0,
        discount_type: discountAmount > 0 ? discType : "none",
        discount_value: discountAmount > 0 ? discNum : 0,
        customer_name: customer.trim(),
        club_member_id: memberId,
        items: cartItems.map((it) => ({ recipe_id: it.id, quantity: it.qty, flavor: it.flavor || "" })),
      });
      toast.success(payment === "Açık Hesap" ? `Açık hesap açıldı — ${formatTRY(grandTotal)} (ödeme bekliyor)` : `Satış tamamlandı — ${formatTRY(grandTotal)}`);
      const warns = r.data.stock_warnings || [];
      if (warns.length) toast.warning(`${warns.length} malzeme eksiye düştü: ${warns.map((w) => `${w.name} (${formatNum(w.stock, 0)} ${w.unit})`).join(", ")}`, { duration: 8000 });
      setCart({});
      setPayment("Nakit");
      setDiscType("none"); setDiscValue(""); setCustomer(""); setMemberId(null); setCashAmt(""); setCardAmt("");
      localStorage.removeItem(CART_KEY);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Satış başarısız"); }
  };

  const exportXlsx = async () => {
    try { await downloadExport("/export/sales", {}, "fithouse_satislar.xlsx"); toast.success("Excel dosyası indirildi"); }
    catch (e) { toast.error("Dışa aktarma başarısız"); }
  };

  return (
    <div className="space-y-6 pb-28 lg:pb-0" data-testid="sales-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Kasa & Satış</h1>
          <p className="mt-1 text-sm text-muted-foreground">Ürün seçin, satış yapın — stok otomatik düşülür</p>
        </div>
        <Button variant="secondary" data-testid="export-sales-btn" onClick={exportXlsx}>
          <FileSpreadsheet className="mr-1.5 h-4 w-4 text-primary" /> Excel'e Aktar
        </Button>
      </div>

      <OpenTabs tabs={openTabs} cartItems={cartItems} cartDiscount={discountAmount > 0 ? { type: discType, value: discNum } : null} onChanged={load}
        onCartConsumed={() => { setCart({}); setDiscType("none"); setDiscValue(""); localStorage.removeItem(CART_KEY); }} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-border bg-card" data-testid="pos-list">
            <div className="sticky top-16 z-10 border-b border-border bg-card/95 p-3 backdrop-blur lg:static">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input className="h-11 pl-9 pr-9 text-base lg:h-10 lg:text-sm" placeholder="Ürün ara… (ad, kategori, çeşit)" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pos-search" />
                {search && <button type="button" className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary" onClick={() => setSearch("")} aria-label="Temizle" data-testid="pos-search-clear"><X className="h-4 w-4" /></button>}
              </div>
              {q && <p className="mt-1.5 text-xs text-muted-foreground" data-testid="pos-search-count">{visibleRowCount} ürün bulundu</p>}
            </div>
            {groupByCategory(Object.values(visibleRecipes.reduce((acc, r) => {
              const key = `${r.product_name.trim().toLowerCase()}|${r.category}`;
              (acc[key] = acc[key] || { name: r.product_name, category: r.category, variants: [] }).variants.push(r);
              return acc;
            }, {}))).map((cg) => [
              <div key={`h-${cg.category}`} className="bg-secondary/30 px-4 py-2 font-heading text-xs font-extrabold uppercase tracking-[0.2em] text-primary" data-testid={`category-header-${cg.category}`}>
                {cg.category} <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-normal normal-case tracking-normal text-muted-foreground">{cg.items.length} ürün</span>
              </div>,
              ...cg.items.map((g) => {
                const vs = sortedVariants(g);
                const single = vs.length === 1;
                return (
                <div key={g.name + g.category} data-testid={`pos-row-${vs[0].id}`}
                  className="flex flex-wrap items-center gap-2 border-b border-border/60 px-4 py-3 last:border-0 hover:bg-secondary/20 lg:py-2">
                  <div className="min-w-0 basis-full break-words lg:flex-1 lg:basis-auto">
                    <span className="font-heading font-bold">{g.name}</span>
                    {single && vs[0].size && <span className="ml-1.5 inline-block whitespace-nowrap rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary">{vs[0].size}</span>}
                    {!single && <span className="ml-1.5 whitespace-nowrap text-xs text-muted-foreground">{vs.length} boy</span>}
                  </div>
                  {single && <span className="ml-auto font-heading font-extrabold text-primary lg:ml-0">{formatTRY(vs[0].selling_price)}</span>}
                  <div className="flex flex-wrap items-center gap-1.5">
                    {vs.map((v) => (
                      v.flavors?.length ? (
                        <div key={v.id} className="flex flex-wrap items-center gap-1">
                          {!single && <span className="whitespace-nowrap rounded-full bg-primary/15 px-2 py-1 text-[11px] font-semibold text-primary">{v.size || "Standart"} · {formatTRY(v.selling_price)}</span>}
                          {v.flavors.map((f) => {
                            const fs = v.flavor_stock?.find((x) => x.flavor === f);
                            return (
                            <button key={f} data-testid={`pos-product-${v.id}-${f}`} onClick={() => add(v.id, f)}
                              title={fs ? `${fs.units ?? "—"} porsiyon kaldı` : ""}
                              className={`min-h-10 rounded-full border px-3 py-2 text-sm font-semibold transition-all active:scale-95 hover:border-primary hover:bg-primary/10 lg:min-h-0 lg:px-2.5 lg:py-1 lg:text-xs ${fs?.low ? "border-destructive/60 bg-destructive/10 text-destructive" : "border-border bg-secondary/40"}`}>
                              {f}{fs && <span className="ml-1 font-mono text-[10px] opacity-70">{fs.units ?? "—"}</span>}
                            </button>
                            );
                          })}
                        </div>
                      ) : single ? (
                        <button key={v.id} data-testid={`pos-product-${v.id}`} onClick={() => add(v.id)} title="Sepete ekle"
                          className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/15 text-primary transition-transform active:scale-95 hover:scale-110 hover:bg-primary/25 lg:h-8 lg:w-8"><Plus className="h-5 w-5 lg:h-4 lg:w-4" /></button>
                      ) : (
                        <button key={v.id} data-testid={`pos-product-${v.id}`} onClick={() => add(v.id)} title="Sepete ekle"
                          className="group flex min-h-10 items-center gap-1.5 rounded-full border border-border bg-secondary/40 px-3 py-2 text-sm font-semibold transition-all active:scale-95 hover:border-primary hover:bg-primary/10 lg:min-h-0 lg:py-1 lg:text-xs">
                          <span>{v.size || "Standart"}</span><span className="font-heading font-extrabold text-primary">{formatTRY(v.selling_price)}</span><Plus className="h-3 w-3 text-primary" />
                        </button>
                      )
                    ))}
                  </div>
                </div>
                );
              }),
            ])}
            {recipes.length === 0 && <p className="p-4 text-sm text-muted-foreground">Önce ürün oluşturun.</p>}
            {recipes.length > 0 && visibleRecipes.length === 0 && <p className="p-4 text-sm text-muted-foreground" data-testid="pos-search-empty">"{search}" ile eşleşen ürün yok.</p>}
          </div>
        </div>

        <div id="pos-cart" className="scroll-mt-20 rounded-xl border border-border bg-card p-5">
          <h3 className="flex items-center justify-between gap-2 font-heading text-lg font-semibold">
            <span className="flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> Sepet</span>
            {cartItems.length > 0 && <button type="button" className="text-xs font-medium text-muted-foreground hover:text-destructive" data-testid="cart-clear-btn" onClick={() => { setCart({}); setDiscType("none"); setDiscValue(""); setCustomer(""); setMemberId(null); setCashAmt(""); setCardAmt(""); localStorage.removeItem(CART_KEY); }}>Sepeti Temizle</button>}
          </h3>
          <div className="mt-4 space-y-2" data-testid="cart-items">
            {cartItems.length === 0 && <p className="text-sm text-muted-foreground">Sepet boş</p>}
            {cartItems.map((it) => (
              <div key={it.key} className="flex items-center gap-2 rounded-lg bg-secondary/50 px-3 py-2">
                <div className="flex-1">
                  <p className="text-sm font-medium">{it.display_name || it.product_name}</p>
                  <p className="text-xs text-muted-foreground">{formatTRY(it.selling_price)}</p>
                </div>
                <Button size="icon" variant="secondary" className="h-10 w-10 lg:h-7 lg:w-7" onClick={() => dec(it.key)} data-testid={`cart-dec-${it.key}`}><Minus className="h-4 w-4 lg:h-3 lg:w-3" /></Button>
                <span className="w-6 text-center font-mono text-base lg:w-5 lg:text-sm">{it.qty}</span>
                <Button size="icon" variant="secondary" className="h-10 w-10 lg:h-7 lg:w-7" onClick={() => add(it.id, it.flavor)} data-testid={`cart-inc-${it.key}`}><Plus className="h-4 w-4 lg:h-3 lg:w-3" /></Button>
              </div>
            ))}
          </div>

          {cartItems.length > 0 && (
            <div className="mt-3 space-y-1 rounded-lg bg-background/50 p-3 text-xs text-muted-foreground">
              <div className="flex justify-between"><span>Toplam Protein</span><span className="font-mono">{formatNum(totals.protein)}g</span></div>
              <div className="flex justify-between"><span>Toplam VP</span><span className="font-mono">{formatNum(totals.vp)}</span></div>
              <div className="flex justify-between"><span>Tahmini Kâr</span><span className="font-mono text-primary">{formatTRY(totals.total - totals.cost)}</span></div>
            </div>
          )}

          <div className="mt-4">
            <Select value={payment} onValueChange={setPayment}>
              <SelectTrigger data-testid="payment-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            {payment === "Açık Hesap" && (
              <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-500" data-testid="open-tab-note">
                Sipariş şimdi kaydedilir, stok düşer; ödeme sonra alınır. Ödeme alınana kadar ciroya/Z raporuna girmez. Müşteri adı veya masa notu isteğe bağlı.
              </p>
            )}
            {payment === "Personel" && (
              <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-500" data-testid="staff-note">
                Personel tüketimi: ciroya ve Z raporuna yansımaz, yalnızca stoktan düşer. Liste değeri {formatTRY(totals.total)}.
              </p>
            )}
            {payment === "Karma" && (
              <div className="mt-2 rounded-lg border border-border bg-background/50 p-3" data-testid="split-payment-box">
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-xs text-muted-foreground">Nakit (₺)</label>
                    <Input type="number" inputMode="decimal" min="0" step="0.01" data-testid="split-cash" value={cashAmt} onChange={(e) => setCash(e.target.value)} className="mt-1 h-8" /></div>
                  <div><label className="text-xs text-muted-foreground">Kredi Kartı (₺)</label>
                    <Input type="number" inputMode="decimal" min="0" step="0.01" data-testid="split-card" value={cardAmt} onChange={(e) => setCard(e.target.value)} className="mt-1 h-8" /></div>
                </div>
                <p className={`mt-2 text-xs ${splitOk ? "text-muted-foreground" : "text-destructive"}`} data-testid="split-status">
                  {splitOk ? `Toplam ${formatTRY(num(cashAmt) + num(cardAmt))} ✓` : `Fark: ${formatTRY(grandTotal - num(cashAmt) - num(cardAmt))} — toplam ${formatTRY(grandTotal)} olmalı`}
                </p>
              </div>
            )}
          </div>

          <div className="mt-4" data-testid="customer-box">
            <label className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <UserRound className="h-4 w-4 text-primary" /> Müşteri (isteğe bağlı)
            </label>
            <CustomerPicker value={customer} memberId={memberId} onChange={(name, id) => { setCustomer(name); setMemberId(id); }} />
          </div>

          <div className="mt-4 rounded-lg border border-border bg-background/50 p-3" data-testid="discount-box">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <BadgePercent className="h-4 w-4 text-primary" /> İndirim
            </div>
            <div className="flex gap-2">
              <div className="flex rounded-md border border-border p-0.5">
                {[["none", "Yok"], ["percent", "%"], ["amount", "₺"]].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`discount-type-${v}`}
                    onClick={() => { setDiscType(v); if (v === "none") setDiscValue(""); }}
                    className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${discType === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                    {l}
                  </button>
                ))}
              </div>
              <Input type="number" inputMode="decimal" min="0" step="0.01" disabled={discType === "none"} data-testid="discount-value"
                placeholder={discType === "percent" ? "örn. 10" : discType === "amount" ? "örn. 25" : "—"}
                value={discValue} onChange={(e) => setDiscValue(e.target.value)} className="h-8 flex-1" />
            </div>
            {discountAmount > 0 && (
              <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                <div className="flex justify-between"><span>Ara Toplam</span><span className="font-mono" data-testid="cart-subtotal">{formatTRY(totals.total)}</span></div>
                <div className="flex justify-between text-destructive"><span>İndirim{discType === "percent" ? ` (%${discNum})` : ""}</span><span className="font-mono" data-testid="cart-discount">−{formatTRY(discountAmount)}</span></div>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
            <span className="text-sm text-muted-foreground">Genel Toplam</span>
            <span className="font-heading text-2xl font-extrabold text-primary" data-testid="cart-total">{formatTRY(grandTotal)}</span>
          </div>
          <Button data-testid="checkout-btn" onClick={checkout} className="mt-4 h-12 w-full text-base font-semibold lg:h-10 lg:text-sm" disabled={cartItems.length === 0 || !splitOk}>
            Satışı Tamamla
          </Button>
        </div>
      </div>
      <MobileCartBar count={cartItems.reduce((a, x) => a + x.qty, 0)} total={formatTRY(grandTotal)} onCheckout={checkout} disabled={!splitOk} cartId="pos-cart" testid="pos-mobile-bar" />

      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-lg font-semibold"><Receipt className="h-5 w-5" /> Son Satışlar</h3>
          <Button variant="ghost" size="sm" asChild data-testid="all-sales-link"><Link to="/satis-gecmisi">Tümünü gör <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
        </div>
        <div className="mt-4"><SalesList sales={history.slice(0, 10)} onChanged={load} /></div>
      </div>
    </div>
  );
}
